import { useEffect, useMemo, useState } from "react";
import ComplianceNotice from "@/components/ComplianceNotice";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ThinkingIndicator } from "@/components/ThinkingIndicator";
import {
  Mic,
  ListChecks,
  Sparkles,
  Copy,
  Wand2,
  Check,
  RotateCw,
  Clock,
} from "lucide-react";

const QUICK_INSTRUCTIONS = [
  "Make it shorter",
  "Make it more personal",
  "More fiery",
  "Add a story about the volunteer fire department",
  "Cut anything too generic",
];

function fmtMinutes(n) {
  return n === 1 ? "1 minute" : `${n} minutes`;
}

export default function SpeechWriter() {
  const [profileReady, setProfileReady] = useState(null);
  const [outputType, setOutputType] = useState("full_speech");
  const [topic, setTopic] = useState("");
  const [minutes, setMinutes] = useState(5);
  const [background, setBackground] = useState("");
  const [result, setResult] = useState(null); // { id, content, word_count, output_type, length_minutes }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Refine
  const [editing, setEditing] = useState(false);
  const [instruction, setInstruction] = useState("");
  const [refining, setRefining] = useState(false);
  const [revision, setRevision] = useState(null); // { original, revised }
  const [accountType, setAccountType] = useState(null);
  const [perspective, setPerspective] = useState("surrogate"); // staffer-only: 'surrogate' | 'candidate'
  const [history, setHistory] = useState([]);

  const refreshHistory = () => {
    api.get("/speeches/history")
      .then(({ data }) => setHistory(Array.isArray(data) ? data : []))
      .catch(() => {});
  };

  useEffect(() => {
    refreshHistory();
  }, []);

  useEffect(() => {
    api
      .get("/campaign")
      .then(({ data }) => {
        const isCommittee = data?.account_type === "committee";
        const isStaffer = data?.account_type === "staffer";
        setAccountType(data?.account_type || null);
        const primary = isCommittee ? data?.committee_name : data?.candidate_name;
        const secondary = isCommittee ? data?.party : data?.office_sought;
        // staffer profile readiness = candidate name + office (the candidate they work for)
        if (isStaffer) {
          setProfileReady(!!(data?.candidate_name && data?.office_sought));
        } else {
          setProfileReady(!!(data && primary && secondary));
        }
      })
      .catch(() => setProfileReady(false));
    // Prefill from campaign profile once on mount. Setters are stable.
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const targetWords = useMemo(() => 130 * minutes, [minutes]);

  const generate = async () => {
    if (!topic.trim()) {
      toast.error("Tell us the topic or occasion.");
      return;
    }
    setError("");
    setLoading(true);
    setResult(null);
    setRevision(null);
    setEditing(false);
    setInstruction("");
    try {
      const { data } = await api.post("/speeches/generate", {
        output_type: outputType,
        topic: topic.trim(),
        length_minutes: minutes,
        background: background.trim(),
        perspective: accountType === "staffer" ? perspective : "candidate",
      });
      setResult(data);
      refreshHistory();
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Couldn't copy. Select the text manually.");
    }
  };

  const submitRefine = async () => {
    if (!instruction.trim() || !result) return;
    setRefining(true);
    setRevision(null);
    try {
      const { data } = await api.post("/speeches/refine", {
        original: result.content,
        instruction: instruction.trim(),
        output_type: result.output_type,
        length_minutes: result.length_minutes,
      });
      setRevision({ original: result.content, revised: data.revised });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setRefining(false);
    }
  };

  const acceptEdit = () => {
    if (!revision) return;
    const newWc = (revision.revised || "").trim().split(/\s+/).filter(Boolean).length;
    setResult((r) => ({ ...r, content: revision.revised, word_count: newWc }));
    setRevision(null);
    setInstruction("");
    setEditing(false);
    toast.success("Edit accepted");
  };

  const keepRefining = () => {
    if (!revision) return;
    const newWc = (revision.revised || "").trim().split(/\s+/).filter(Boolean).length;
    setResult((r) => ({ ...r, content: revision.revised, word_count: newWc }));
    setRevision(null);
    setInstruction("");
  };

  if (profileReady === false) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <div className="bg-white border border-slate-200 rounded-xl p-10 shadow-sm">
          <h1 className="font-display text-2xl font-bold text-flag-navy">Finish your intake first</h1>
          <p className="text-slate-600 mt-2">We use your saved profile to ground every speech.</p>
          <Link to="/intake" data-testid="speech-no-profile-cta">
            <Button className="mt-6 bg-flag-red hover:bg-flag-red-hover text-white font-bold h-11 px-5">
              Go to intake
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div>
        <p className="text-sm font-semibold uppercase tracking-wider text-flag-red">Speech Writer</p>
        <h1 className="font-display text-3xl sm:text-4xl font-black text-flag-navy mt-1">Write the room.</h1>
        <p className="text-slate-600 mt-2">A speech to read aloud or a scannable cheat sheet — grounded in your profile, written in your voice.</p>
      </div>

      <ComplianceNotice kind="ai" className="mt-4" />

      <div className="mt-8 grid md:grid-cols-2 gap-6">
        {/* Form */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 sm:p-8 space-y-5">
          {accountType === "staffer" && (
            <div data-testid="speech-perspective-card">
              <Label className="text-sm font-semibold text-slate-800">Perspective</Label>
              <div className="mt-2 grid grid-cols-2 gap-2" data-testid="speech-perspective-group">
                <button
                  type="button"
                  onClick={() => setPerspective("surrogate")}
                  data-testid="speech-perspective-surrogate"
                  className={`text-left p-3 rounded-lg border-2 transition-colors ${
                    perspective === "surrogate" ? "border-flag-red bg-flag-red/5" : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <div className="font-display font-bold text-flag-navy text-sm">Surrogate speech</div>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed">You or a supporter speaking on the candidate's behalf.</p>
                </button>
                <button
                  type="button"
                  onClick={() => setPerspective("candidate")}
                  data-testid="speech-perspective-candidate"
                  className={`text-left p-3 rounded-lg border-2 transition-colors ${
                    perspective === "candidate" ? "border-flag-red bg-flag-red/5" : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <div className="font-display font-bold text-flag-navy text-sm">Speech for the candidate</div>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed">Drafting a speech for the candidate to deliver in first person.</p>
                </button>
              </div>
            </div>
          )}
          <div>
            <Label className="text-sm font-semibold text-slate-800">Output type</Label>
            <div className="mt-2 grid grid-cols-2 gap-2" data-testid="speech-output-type-group">
              <button
                type="button"
                onClick={() => setOutputType("full_speech")}
                data-testid="speech-type-full-speech"
                className={`text-left p-4 rounded-lg border-2 transition-colors ${
                  outputType === "full_speech"
                    ? "border-flag-red bg-flag-red/5"
                    : "border-slate-200 bg-white hover:border-slate-300"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Mic className="h-4 w-4 text-flag-red" />
                  <span className="font-display font-bold text-flag-navy">Full Speech</span>
                </div>
                <p className="text-xs text-slate-600 mt-1 leading-relaxed">Flowing prose to read aloud.</p>
              </button>
              <button
                type="button"
                onClick={() => setOutputType("talking_points")}
                data-testid="speech-type-talking-points"
                className={`text-left p-4 rounded-lg border-2 transition-colors ${
                  outputType === "talking_points"
                    ? "border-flag-red bg-flag-red/5"
                    : "border-slate-200 bg-white hover:border-slate-300"
                }`}
              >
                <div className="flex items-center gap-2">
                  <ListChecks className="h-4 w-4 text-flag-red" />
                  <span className="font-display font-bold text-flag-navy">Talking Points</span>
                </div>
                <p className="text-xs text-slate-600 mt-1 leading-relaxed">A bulleted cheat sheet.</p>
              </button>
            </div>
          </div>

          <div>
            <Label className="text-sm font-semibold text-slate-800">Topic or occasion *</Label>
            <Input
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              maxLength={500}
              placeholder='e.g. "Rotary Club lunch", "Memorial Day ceremony", "Candidate forum opening statement"'
              className="mt-1 h-11 text-base"
              data-testid="speech-topic-input"
            />
          </div>

          <div>
            <Label className="text-sm font-semibold text-slate-800">Target length</Label>
            <div className="mt-1 flex items-center gap-3">
              <Select value={String(minutes)} onValueChange={(v) => setMinutes(parseInt(v, 10))}>
                <SelectTrigger className="h-11 text-base" data-testid="speech-minutes-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 5].map((m) => (
                    <SelectItem key={m} value={String(m)} data-testid={`speech-minutes-option-${m}`}>
                      {fmtMinutes(m)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {outputType === "full_speech" && (
                <div className="text-xs text-slate-500 flex items-center gap-1.5" data-testid="speech-target-words">
                  <Clock className="h-3.5 w-3.5" /> ≈ {targetWords} words
                </div>
              )}
            </div>
          </div>

          <div>
            <Label className="text-sm font-semibold text-slate-800">Background or your views on this topic (optional)</Label>
            <Textarea
              value={background}
              onChange={(e) => setBackground(e.target.value)}
              rows={5}
              maxLength={4000}
              placeholder="What do you want to say about this? Any anecdotes, positions, or context that should land?"
              className="mt-1 text-base"
              data-testid="speech-background-input"
            />
          </div>

          <Button
            onClick={generate}
            disabled={loading || !topic.trim()}
            data-testid="speech-generate-btn"
            className="w-full h-12 bg-flag-red hover:bg-flag-red-hover text-white font-bold text-base"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            {loading ? "Writing…" : result ? "Regenerate" : "Generate"}
          </Button>

          {error && (
            <div className="text-sm text-flag-red bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="speech-error">
              {error}
            </div>
          )}
        </div>

        {/* Output */}
        <div className="space-y-4">
          {!result && !loading && (
            <>
              <div className="bg-white border border-dashed border-slate-300 rounded-xl p-8 text-center text-slate-500" data-testid="speech-empty">
                Your speech will appear here.
              </div>
              {history.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="speech-history">
                  <div className="flex items-center gap-2 mb-3">
                    <Clock className="h-4 w-4 text-flag-red" />
                    <h3 className="font-display font-bold text-flag-navy text-sm uppercase tracking-wider">Recent</h3>
                  </div>
                  <ul className="space-y-2">
                    {history.slice(0, 5).map((h) => (
                      <li key={h.id}>
                        <button
                          type="button"
                          onClick={() => {
                            setOutputType(h.output_type || "full_speech");
                            setTopic(h.topic || "");
                            setMinutes(h.length_minutes || 5);
                            setBackground(h.background || "");
                            setResult({
                              id: h.id,
                              content: h.content,
                              word_count: h.word_count,
                              output_type: h.output_type,
                              length_minutes: h.length_minutes,
                            });
                          }}
                          data-testid={`speech-history-${h.id}`}
                          className="w-full text-left p-3 rounded-md border border-slate-200 hover:border-flag-red hover:bg-flag-red/5 transition-colors"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-semibold text-flag-navy text-sm leading-snug line-clamp-1">{h.topic || "Untitled"}</span>
                            <span className="text-xs uppercase tracking-wider font-bold text-flag-red shrink-0">
                              {h.output_type === "talking_points" ? "Talking points" : "Speech"}
                            </span>
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            {fmtMinutes(h.length_minutes || 5)} · {h.word_count || 0} words
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
          {loading && (
            <ThinkingIndicator
              label={outputType === "full_speech" ? "Writing your speech" : "Drafting your talking points"}
              testid="speech-loading"
            />
          )}
          {result && (
            <>
            <div
              className="bg-amber-50 border-2 border-amber-300 rounded-xl px-4 py-3 flex items-start gap-2.5 mb-3"
              data-testid="speech-review-notice"
              role="note"
            >
              <span aria-hidden="true" className="text-amber-600 text-lg leading-none mt-0.5">⚠</span>
              <p className="text-xs text-amber-900 leading-snug">
                <strong>Review before delivering.</strong> Read the speech through and verify every personal detail, name, date, location, and statistic is accurate. Any bracketed placeholders like <code className="bg-amber-100 px-1 rounded">[ADD YOUR PERSONAL MEMORY HERE]</code> must be filled in by you — the AI intentionally does not invent first-person experiences it wasn&rsquo;t told.
              </p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 animate-fade-up" data-testid="speech-result">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-flag-red/10 text-flag-red">
                    {result.output_type === "full_speech" ? <Mic className="h-4 w-4" /> : <ListChecks className="h-4 w-4" />}
                  </span>
                  <div>
                    <div className="font-display font-bold text-flag-navy">
                      {result.output_type === "full_speech" ? "Full Speech" : "Talking Points"}
                    </div>
                    <div className="text-xs text-slate-500" data-testid="speech-meta">
                      {fmtMinutes(result.length_minutes)} · {result.word_count} words
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => copy(result.content)}
                    data-testid="speech-copy-btn"
                    className="h-9 border-2 font-semibold"
                  >
                    <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
                  </Button>
                  <Button
                    variant={editing ? "secondary" : "outline"}
                    onClick={() => setEditing((v) => !v)}
                    data-testid="speech-edit-toggle"
                    className={`h-9 font-semibold ${editing ? "bg-flag-navy text-white hover:bg-flag-navy-2" : "border-2"}`}
                  >
                    <Wand2 className="h-3.5 w-3.5 mr-1.5" /> {editing ? "Close" : "Suggest edit"}
                  </Button>
                </div>
              </div>

              <Textarea
                value={result.content}
                onChange={(e) => {
                  const wc = e.target.value.trim().split(/\s+/).filter(Boolean).length;
                  setResult((r) => ({ ...r, content: e.target.value, word_count: wc }));
                }}
                rows={result.output_type === "full_speech" ? 18 : 14}
                className="mt-4 text-base leading-relaxed font-mono"
                data-testid="speech-content-textarea"
              />

              {editing && (
                <div className="mt-4 border-t border-slate-100 pt-4 space-y-3 animate-fade-up" data-testid="speech-edit-panel">
                  <Label className="text-sm font-semibold text-slate-800">What should change?</Label>
                  <Textarea
                    rows={2}
                    value={instruction}
                    onChange={(e) => setInstruction(e.target.value)}
                    placeholder={`e.g. "Make it more personal — add a story about the fire department"`}
                    className="text-base"
                    data-testid="speech-edit-instruction"
                  />
                  <div className="flex flex-wrap gap-2">
                    {QUICK_INSTRUCTIONS.map((q) => (
                      <button
                        key={q}
                        type="button"
                        onClick={() => setInstruction(q)}
                        data-testid={`speech-edit-chip-${q.replace(/\s+/g, "-").toLowerCase()}`}
                        className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                  <Button
                    onClick={submitRefine}
                    disabled={refining || !instruction.trim()}
                    data-testid="speech-edit-submit"
                    className="h-10 px-4 bg-flag-navy hover:bg-flag-navy-2 text-white font-bold"
                  >
                    <Wand2 className="h-3.5 w-3.5 mr-1.5" />
                    {refining ? "Revising…" : revision ? "Re-run" : "Suggest edit"}
                  </Button>

                  {revision && (
                    <div className="mt-3 grid sm:grid-cols-2 gap-3 animate-fade-up" data-testid="speech-edit-compare">
                      <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/60 max-h-80 overflow-auto">
                        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Original</div>
                        <p
                          className="mt-2 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed"
                          data-testid="speech-edit-original"
                        >
                          {revision.original}
                        </p>
                      </div>
                      <div className="border-2 border-flag-red rounded-lg p-3 bg-red-50/40 max-h-80 overflow-auto">
                        <div className="text-[11px] font-bold uppercase tracking-wider text-flag-red">Proposed edit</div>
                        <p
                          className="mt-2 text-sm text-slate-900 whitespace-pre-wrap leading-relaxed"
                          data-testid="speech-edit-revised"
                        >
                          {revision.revised}
                        </p>
                      </div>
                      <div className="sm:col-span-2 flex flex-wrap items-center gap-2 pt-1">
                        <Button
                          onClick={acceptEdit}
                          data-testid="speech-edit-accept"
                          className="h-10 px-4 bg-flag-red hover:bg-flag-red-hover text-white font-bold"
                        >
                          <Check className="h-4 w-4 mr-1.5" /> Accept edit
                        </Button>
                        <Button
                          variant="outline"
                          onClick={keepRefining}
                          data-testid="speech-edit-keep-refining"
                          className="h-10 px-4 border-2 font-semibold"
                        >
                          <RotateCw className="h-4 w-4 mr-1.5" /> Keep refining
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => setRevision(null)}
                          data-testid="speech-edit-discard"
                          className="h-10 px-3 font-semibold text-slate-600 hover:text-flag-red"
                        >
                          Discard
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <p className="mt-3 text-xs text-slate-500">Saved to your History automatically.</p>
            </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
